import type { FieldDefinition, Sort } from "@estelle/domain";

/**
 * The sort state and the transitions over it.
 *
 * `Sort[]` rather than a single `Sort` because an ERP list routinely needs
 * "by state, then by date" — a single-column sort would force a second
 * control just to express ordering among ties. Order in the array is
 * significance: index 0 sorts first.
 */

/** A column's place in the active sort, or `null` when it is not sorted. */
export interface SortPosition {
  direction: Sort["direction"];
  /** 1-based rank among the active sort columns, for the "2 ↓" ordinal. */
  index: number;
}

/** Where `column` sits in `sort`, or `null` if it is not part of it. */
export function sortPositionOf(
  sort: Sort[],
  column: string,
): SortPosition | null {
  const index = sort.findIndex((s) => s.column === column);
  if (index === -1) return null;
  const entry = sort[index];
  if (!entry) return null;
  return { direction: entry.direction, index: index + 1 };
}

/**
 * Cycle a column through asc → desc → off.
 *
 * Off is a real third state, not just asc/desc, because a header someone
 * clicked by accident (or a sort they are done with) needs a way back to
 * "unsorted" without touching every other column's toggle. Two states
 * (asc/desc only) would strand a user on whichever direction they last
 * picked with no way to clear it short of clearing the whole sort.
 *
 * `additive` is shift-click: the column is added to (or cycled within) the
 * existing sort instead of replacing it. A plain click always collapses to
 * a single-column sort — that is the common case, and multi-column sort
 * should be an opt-in gesture rather than something a user falls into by
 * clicking two headers in a row.
 *
 * Sorting by a field whose `sortable` is not `true` is a no-op: the caller
 * is expected to not render a sort affordance for such a field at all, but
 * this guards the state transition itself so a stray call cannot corrupt it.
 */
export function toggleSort(
  current: Sort[],
  field: FieldDefinition,
  additive: boolean,
): Sort[] {
  if (!field.sortable) return current;
  const column = field.name;
  const existing = current.find((s) => s.column === column);
  const next = nextDirection(existing?.direction);

  if (!additive) {
    return next === null ? [] : [{ column, direction: next }];
  }

  const withoutColumn = current.filter((s) => s.column !== column);
  if (next === null) return withoutColumn;
  return [...withoutColumn, { column, direction: next }];
}

function nextDirection(
  current: Sort["direction"] | undefined,
): Sort["direction"] | null {
  if (current === undefined) return "asc";
  if (current === "asc") return "desc";
  return null;
}

/**
 * The URL form: `column:direction` pairs, comma-joined.
 *
 * Column names are stable field keys, so no encoding is needed beyond the
 * separators. An empty sort emits nothing rather than an empty parameter.
 */
export function encodeSort(sort: Sort[]): string | undefined {
  if (sort.length === 0) return undefined;
  return sort.map((entry) => `${entry.column}:${entry.direction}`).join(",");
}

/**
 * Read a sort back, dropping anything the module cannot sort by.
 *
 * A link written before a field was renamed, or one hand-edited into
 * nonsense, should sort by whatever is still valid rather than throwing away
 * the whole list or the whole sort.
 */
export function decodeSort(
  raw: string | undefined,
  fields: FieldDefinition[],
): Sort[] {
  if (!raw) return [];
  const sortable = new Set(
    fields.filter((field) => field.sortable === true).map((f) => f.name),
  );

  return raw
    .split(",")
    .map((pair) => {
      const [column, direction] = pair.split(":");
      if (!column || !sortable.has(column)) return null;
      return {
        column,
        direction: direction === "asc" ? ("asc" as const) : ("desc" as const),
      };
    })
    .filter((entry): entry is Sort => entry !== null);
}
